import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {ConfigProvider, message} from "antd";
import {set} from "lodash";
import {NOOP} from "../../utils/NOOP.jsx";
import {AllowedSystemIdBudgetLoadPaymentType, PaymentType, ValidateBookingError} from "../../consts.js";
import {FormModal} from "../../components/index.jsx";
import {PaymentInput} from "../../marketplace/MarketplaceBooking/Payment/PaymentInput";
import {SeparatorLine} from "../../components/SeparatorLine/index.jsx";
import {AppContext} from "../../AppContext.jsx";
import {getThemeByOrigin} from "../../utils.js";
import {StringBuilder} from "../../AppUtils.js";
import {MIN_SELF_BUDGET_LOAD, MIN_SELF_BUDGET_LOAD_BY_SYSTEM_ID} from "./consts.js";
import {TEN_BIS_CARD_LENGTH} from "../../marketplace/MarketplaceBooking/consts.jsx";
import {LoadBudgetFullDescriptionImage, LoadBudgetShortDescriptionImage} from "../../images/Images.jsx";
import "./company-budget-load-modal.css";

export const CompanyBudgetLoadModal = ({
    visible,
    onClose,
    onSave = NOOP,
    validationFunction,
    budgetLoadRequestAmount,
    isLoading,
    me = {},
    company = {},
    services = []
}) => {
    const [formValues, setFormValues] = useState({});
    const [paymentType, setPaymentType] = useState(PaymentType.Card);
    const [creditCard, setCreditCard] = useState(false);
    const [shouldShowFullDescription, setShouldShowFullDescription] = useState(false);

    const onFormValueChange = useCallback(
        (field, val) => {
            setFormValues(prevValues => set({...prevValues}, field, val));
        },
        [setFormValues]
    );

    useEffect(() => {
        if (budgetLoadRequestAmount) {
            onFormValueChange("amount", budgetLoadRequestAmount);
        }
    }, [budgetLoadRequestAmount, visible]);

    const closeHandler = useCallback(() => {
        setFormValues({});
        setShouldShowFullDescription(false);
        onClose();
    }, [onClose]);

    const minSelfBudgetLoad = useMemo(
        () => MIN_SELF_BUDGET_LOAD_BY_SYSTEM_ID[getThemeByOrigin(window.location.origin)] ?? MIN_SELF_BUDGET_LOAD,
        []
    );

    return (
        <ConfigProvider direction="rtl">
            <FormModal
                className="company-budget-load-container-modal"
                visible={visible}
                onClose={closeHandler}
                header={"טעינת תקציב 🫰"}
                shouldShowHeaderDivider={false}
                confirmButtonTitle={
                    !isNaN(Number(formValues?.amount)) && Number(formValues?.amount) >= minSelfBudgetLoad
                        ? `שמירה (${Number(formValues?.amount).toLocaleString()} ש"ח)`
                        : "שמירה"
                }
                subTitle={
                    <>
                        <div>באפשרותכם להטעין תקציב ולשלם במרוכז עבור הזמנות במערכת עד לגובה התקציב שנטען</div>
                        <img
                            onClick={() => setShouldShowFullDescription(true)}
                            src={
                                shouldShowFullDescription
                                    ? LoadBudgetFullDescriptionImage[getThemeByOrigin(window.location.origin)] ??
                                      LoadBudgetFullDescriptionImage.image
                                    : LoadBudgetShortDescriptionImage[getThemeByOrigin(window.location.origin)] ??
                                      LoadBudgetShortDescriptionImage.image
                            }
                            alt="modal image"
                            className="form-modal-image"
                            style={{cursor: "pointer"}}
                        />
                    </>
                }
                content={formValues}
                onUpdate={(allValues, changedField) => {
                    const [fieldKey, fieldValue] = Object.entries(changedField)[0];
                    onFormValueChange(fieldKey, fieldValue);
                }}
                modalWidth={700}
                onSave={({amount}) => onSave({amount, ...formValues})}
                validationFunction={({amount, invoiceComment, invoicePONumber}) => {
                    if (paymentType === PaymentType.Card && !Object.keys(creditCard ?? {})?.length) {
                        message.error(
                            "שגיאה! לא הוכנסו פרטי כרטיס אשראי | לסיוע בהשלמת ההזמנה, ניתן לפנות אלינו בוואטסאפ"
                        );
                        return false;
                    }

                    if (
                        paymentType === PaymentType.TenBisCard &&
                        (formValues?.tenBisCreditCard?.length ?? 0) < TEN_BIS_CARD_LENGTH
                    ) {
                        message.error(
                            new StringBuilder("שגיאה!")
                                .append("יש להכניס כרטיס תן ביס באורך")
                                .append(TEN_BIS_CARD_LENGTH)
                                .append("ספרות")
                                .toString()
                        );
                        return false;
                    }

                    if (
                        paymentType === PaymentType.BankTransfer &&
                        !formValues.invoicePONumber?.length &&
                        (company?.requirePONumber || me?.isMustProvidePurchaseOrderNumber)
                    ) {
                        message.error(ValidateBookingError.NO_INVOICE_PO_NUMBER);
                        return false;
                    }

                    return validationFunction({
                        ...formValues,
                        amount,
                        paymentType,
                        ...(paymentType !== PaymentType.BankTransfer
                            ? {
                                  invoiceComment: null,
                                  invoicePONumber: null
                              }
                            : {}),
                        ...(paymentType === PaymentType.Card ? {creditCard} : {})
                    });
                }}
                isConfirmButtonLoading={isLoading}
                fields={[
                    {
                        name: "amount",
                        rowClassName: "amount-input-container",
                        label: "סכום לטעינה",
                        info: "יש להקליד את הסכום הרצוי לטעינה",
                        placeholder: "הזן סכום לטעינה",
                        rules: [
                            {
                                validator: (_, amount) => {
                                    const parsedAmount = Number(amount);

                                    if (isNaN(parsedAmount)) {
                                        return Promise.reject("אנא הקלידו מספרים בלבד");
                                    }

                                    return parsedAmount < minSelfBudgetLoad && !me?.isAdmin
                                        ? Promise.reject(
                                              new StringBuilder()
                                                  .append("אנא הקלידו את הסכום הרצוי לטעינה")
                                                  .append("-")
                                                  .append("מינימום")
                                                  .append(minSelfBudgetLoad)
                                                  .append("₪")
                                                  .append("לטעינה")
                                                  .toString()
                                          )
                                        : Promise.resolve();
                                }
                            }
                        ]
                    },
                    {
                        type: "custom",
                        name: "SeparatorLine",
                        rowClassName: "separator-line-container",
                        customComponent: <SeparatorLine text="אמצעי טעינה לתשלום" />
                    },
                    {
                        type: "custom",
                        name: "payment",
                        customComponent: (
                            <PaymentInput
                                me={me}
                                company={company}
                                onInvoiceCommentChange={newInvoiceComment =>
                                    onFormValueChange("invoiceComment", newInvoiceComment)
                                }
                                invoiceComment={formValues.invoiceComment}
                                onInvoicePONumberChange={newPONumber =>
                                    onFormValueChange("invoicePONumber", newPONumber)
                                }
                                invoicePONumber={formValues.invoicePONumber}
                                onSeparateInvoiceChange={newVal => onFormValueChange("separateInvoice", newVal)}
                                allowedPaymentMethods={
                                    AllowedSystemIdBudgetLoadPaymentType[getThemeByOrigin(window.location.origin)]
                                }
                                paymentType={paymentType}
                                setPaymentType={setPaymentType}
                                separateInvoice={formValues?.separateInvoice}
                                creditCard={creditCard}
                                creditCardToolTipInfo={
                                    <>
                                        <div>
                                            <p className="payment-input-tooltip-text">
                                                בחירה בכרטיס אשראי כאמצעי תשלום לעסקה מאפשרת לכם לשלם על העסקה הספציפית
                                                הזו בכרטיס אשראי המוזן כולל הנפקת חשבונית מס/קבלה.
                                            </p>
                                        </div>
                                    </>
                                }
                                setCreditCard={setCreditCard}
                                budgetCategoriesPriority={null}
                                onBudgetCategoriesPriorityChange={NOOP}
                                updateFormValues={(formField, formValue) => onFormValueChange(formField, formValue)}
                                tenBisCreditCard={formValues?.tenBisCreditCard}
                                services={services}
                            />
                        )
                    }
                ]}
            />
        </ConfigProvider>
    );
};
